import i18n from "i18next";
import { initReactI18next } from "react-i18next";

const resources = {
  de: {
    translation: {
      app: { name: "Autordo", tagline: "Werkstattmanagement" },
      nav: {
        dashboard: "Dashboard",
        planning: "Wochenplanung",
        contracts: "Aufträge",
        customers: "Kunden",
        vehicles: "Fahrzeuge",
        employees: "Mitarbeiter",
        absences: "Abwesenheiten",
        templates: "Checklisten",
        settings: "Einstellungen",
        logout: "Abmelden",
      },
      auth: {
        signin: "Anmelden",
        signin_subtitle: "Bei Autordo anmelden",
        hero_title: "Klüger planen.\nSchneller schrauben.",
        hero_subtitle: "Aufträge, Auslastung und Prüflisten in einem professionellen Cockpit.",
        hourly_rate_placeholder: "Stundensatz",
        email: "E-Mail",
        password: "Passwort",
        login_btn: "Einloggen",
        invalid: "Ungültige Anmeldedaten",
        register: "Registrieren",
        first_name: "Vorname",
        last_name: "Nachname",
        company_name: "Firmenname",
        create_workspace: "Workspace anlegen",
        demo_account: "Demo-Account starten",
        demo_hint: "Testet Autordo mit Beispieldaten. Wird bei jedem Demo-Login zurückgesetzt.",
      },
      common: {
        save: "Speichern", cancel: "Abbrechen", delete: "Löschen", edit: "Bearbeiten",
        create: "Erstellen", new: "Neu", search: "Suchen", actions: "Aktionen",
        yes: "Ja", no: "Nein", confirm: "Bestätigen", back: "Zurück", print: "Drucken",
        name: "Name", phone: "Telefon", email: "E-Mail", address: "Adresse",
        loading: "Laden...", empty: "Keine Einträge", status: "Status", priority: "Priorität",
        notes: "Notizen", description: "Beschreibung",
        close: "Schließen", clear_filters: "Filter löschen",
        dialog_description: "Dialog",
        hour_short: "h", hours: "Stunden", qty: "Menge", unit: "Einheit",
        unit_price: "Einzelpreis", total: "Gesamt",
        day_short_mon: "Mo", day_short_tue: "Di", day_short_wed: "Mi",
        day_short_thu: "Do", day_short_fri: "Fr", day_short_sat: "Sa", day_short_sun: "So",
      },
      breaks: {
        error_range: "Endzeit muss nach der Startzeit liegen",
        error_outside: "Pause liegt außerhalb der Arbeitszeit {{start}}–{{end}}",
        error_no_days: "Mindestens einen Wochentag auswählen",
        error_overlap: "Pause überschneidet sich mit einer anderen am selben Tag",
        error_duplicate: "Doppelte Pause",
      },
      workitem: {
        labor: "Arbeit", part: "Material", flat: "Pauschal", other: "Sonstiges",
        fixed_price: "Festpreis",
        subtotal_labor: "Arbeit", subtotal_parts: "Material",
        subtotal_flat: "Pauschal", subtotal_other: "Sonstiges",
        grand_total: "Gesamtsumme",
        sub_items: "Unterpositionen", add_sub_item: "Unterposition hinzufügen",
        sub_total: "Summe Unterpositionen", auto_from_sub: "Automatisch aus Unterpositionen",
      },
      pricing: { net: "Netto", vat: "MwSt.", gross: "Brutto" },
      notes: {
        title: "Interne Notizen",
        add: "Notiz hinzufügen",
        placeholder: "Notiz schreiben…",
        empty: "Noch keine Notizen.",
        pinned: "Angeheftet",
        pin: "Anheften",
        unpin: "Nicht mehr anheften",
        author: "Autor",
        by: "von",
      },
      emails: {
        title: "E-Mail",
        send: "E-Mail senden",
        send_short: "Senden",
        template: "Vorlage",
        subject: "Betreff",
        body: "Nachricht",
        preview_note: "Signatur und Layout werden automatisch angehängt.",
        no_email: "Dieser Kunde hat keine E-Mail-Adresse hinterlegt.",
        sent: "E-Mail wurde versendet",
        log_title: "Versand-Protokoll",
        log_empty: "Es wurden noch keine E-Mails versendet.",
        log_recipient: "Empfänger",
        log_subject: "Betreff",
        log_when: "Versendet am",
        log_by: "Von",
        log_status: "Status",
        log_auto: "Automatisch (HU)",
        log_manual: "Manuell",
        placeholders: "Platzhalter",
        placeholders_hint: "Verfügbar: {customer_name}, {vehicle}, {license_plate}, {hu_date}, {workshop_name}, {contract_number}",
        run_hu_now: "HU-Erinnerungen jetzt prüfen",
        hu_reminder: "HU-Erinnerung",
        attach_pdf: "PDF anhängen",
        pdf_mode_contract: "Auftrag",
        pdf_mode_invoice: "Rechnung",
        download_pdf: "PDF herunterladen",
        templates_title: "E-Mail-Vorlagen",
        templates_hint: "Betreff und Text jeder Vorlage. Unterstützt Platzhalter wie {customer_name}.",
        template_name: "Vorlagenname",
        template_new: "Neue Vorlage",
        template_saved: "Vorlage gespeichert",
        system_template: "Systemvorlage",
      },
      vehicle_extra: {
        next_hu_date: "Nächste HU",
        hu_due_in: "HU in {{days}} Tagen",
        hu_overdue: "HU überfällig",
      },
      confirm: {
        title: "Sind Sie sicher?",
        description: "Möchten Sie diesen Eintrag wirklich löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.",
      },
      dashboard: {
        title: "Dashboard",
        heading: "Werkstatt-Überblick",
        total: "Aufträge", new: "Neu", in_progress: "In Arbeit",
        finished: "Fertig", billed: "Abgerechnet",
        capacity: "Werkstattkapazität", planned_aw: "Arbeitszeit heute",
        waiting: "Wartet",
        period_week: "Woche", period_month: "Monat", period_year: "Jahr",
        occupancy: "Auslastung", overloaded: "Überlastete Mitarbeiter",
        today: "Heutige Aufträge",
      },
      planning: {
        title: "Wochenplanung", week_of: "Woche ab", prev: "Vorherige", next: "Nächste",
        capacity: "Kapazität", assigned: "Zugewiesen", today: "Heute",
        unassigned: "Nicht zugewiesen",
        view_week: "Woche", view_day: "Tag", view_agenda: "Agenda",
        sum_capacity: "Gesamtkapazität", sum_planned: "Geplante Arbeit",
        sum_occupancy: "Auslastung", sum_active: "Aktive Mitarbeiter",
        sum_contracts: "Aufträge", sum_overloaded: "Überlastet",
        create_contract: "Vertrag erstellen", hours_short: "h",
      },
      contracts: {
        title: "Aufträge", number: "Auftragsnr.", customer: "Kunde", vehicle: "Fahrzeug",
        title_placeholder: "Inspektion + Ölwechsel...",
        plate: "Kennzeichen", total_aw: "Gesamt AW", working_time: "Arbeitszeit", total_price: "Gesamtpreis", planned: "Geplant",
        new_contract: "Neuer Auftrag", details: "Auftragsdetails",
        work_items: "Arbeitspositionen", checklist: "Checkliste", audit: "Verlauf",
        add_work_item: "Position hinzufügen",
        completion_prompt: "Alle Arbeiten sind erledigt. Auftrag als 'Fertig' markieren?",
        plan_date: "Geplantes Datum", plan_time: "Geplante Uhrzeit",
        assigned_to: "Zugewiesen an", apply_template: "Vorlage anwenden",
        price: "Preis", payment: "Zahlung",
        payment_status: { unpaid: "Offen", deposit: "Anzahlung", paid: "Bezahlt" },
        deposit_amount: "Anzahlung", remaining: "Restbetrag",
      },
      statuses: {
        new: "Neu", planned: "Geplant", in_progress: "In Arbeit",
        waiting_parts: "Wartet auf Teile", waiting_customer: "Wartet auf Kunde",
        finished: "Fertig", billed: "Abgerechnet", cancelled: "Storniert",
      },
      priorities: { low: "Niedrig", normal: "Normal", high: "Hoch", urgent: "Dringend" },
      checklist: {
        io: "I.O.", nio: "N.I.O.", unchecked: "Offen",
        items: {
          engine_oil: "Motorölstand",
          coolant: "Kühlmittelstand",
          brake_fluid: "Bremsflüssigkeit",
          brakes_front: "Vorderradbremsen",
          brakes_rear: "Hinterradbremsen",
          tire_front_left: "Reifen vorne links",
          tire_front_right: "Reifen vorne rechts",
          tire_rear_left: "Reifen hinten links",
          tire_rear_right: "Reifen hinten rechts",
          lights: "Beleuchtung",
          wipers: "Scheibenwischer",
          battery: "Batterie",
          diagnostic: "Diagnosescan",
          test_drive: "Probefahrt",
        },
        templates: { standard: "Standard Inspektion" },
      },
      absence: {
        title: "Abwesenheit", new: "Neue Abwesenheit", reason: "Grund",
        start: "Von", end: "Bis", note: "Notiz", day_off: "Frei",
        reasons: { vacation: "Urlaub", sick: "Krank", training: "Schulung", day_off: "Frei", private: "Privat", other: "Sonstige" },
        unavailable: "Nicht verfügbar",
        confirm_override: "Mitarbeiter ist abwesend. Trotzdem zuweisen?",
        edit: "Abwesenheit bearbeiten",
        view_month: "Monat", view_year: "Jahr",
        error_range: "Enddatum liegt vor Startdatum",
        confirm_delete: "Abwesenheit löschen?",
        confirm_delete_day: "Diesen Tag entfernen?",
        day_trim_hint: "Tipp: Klick auf einen einzelnen Tag im Kalender entfernt nur diesen Tag.",
        blocked_assignment: "Mitarbeiter ist abwesend – Zuweisung nicht möglich",
        blocked_day_off: "Arbeitsfreier Tag – Zuweisung nicht möglich",
        weekend_day_off: "Arbeitsfreier Tag",
      },
      employees: {
        title: "Mitarbeiter", first_name: "Vorname", last_name: "Nachname",
        role: "Rolle", working_days: "Arbeitstage", start: "Beginn", end: "Ende",
        break_start: "Pause Beginn", break_end: "Pause Ende",
        daily_capacity: "Tageskapazität", active: "Aktiv",
        upcoming_absences: "Kommende Abwesenheiten",
        user_account: "Benutzerkonto",
        user_account_hint: "Login für diesen Mitarbeiter erstellen",
        role_mechanic: "Mechaniker", role_admin: "Admin",
        show_in_planning: "In der Wochenplanung anzeigen",
        show_in_planning_hint: "Owner und Admins werden ebenfalls als Mitarbeiter im Planungsraster angezeigt, wenn aktiviert.",
      },
      customers: { title: "Kunden", new_customer: "Neuer Kunde", linked_vehicles: "Verknüpfte Fahrzeuge", linked_contracts: "Aufträge", detail: "Kundendetails" },
      vehicles: {
        title: "Fahrzeuge", brand: "Marke", model: "Modell", plate: "Kennzeichen",
        vin: "VIN", mileage: "Kilometerstand", first_registration: "Erstzulassung",
        fuel: "Kraftstoff", engine: "Motor", new_vehicle: "Neues Fahrzeug",
      },
      templates: {
        title: "Checklisten-Vorlagen", new_template: "Neue Vorlage",
        items: "Positionen", apply: "Anwenden",
      },
      settings: {
        title: "Einstellungen", workshop: "Werkstatt", workshop_name: "Werkstattname",        working_hours: "Arbeitszeiten", default_language: "Standardsprache",
        users: "Benutzerverwaltung",
        tab_workshop: "Werkstatt", tab_users: "Benutzer",
        tab_billing: "Abonnement", tab_discount: "Rabattcodes",
        time_display: "Zeitanzeige",
        time_display_aw: "Nur AW (z. B. 80 AW)",
        time_display_hours: "Nur Stunden (z. B. 8,0 h)",
        time_display_both: "AW und Stunden (z. B. 80 AW / 8,0 h)",
        time_display_hint: "10 AW = 1 Stunde. Die interne Berechnung bleibt gleich, nur die Anzeige ändert sich.",
        hourly_rate: "Stundensatz",
        hourly_rate_hint: "Wird als Standardpreis für Arbeitspositionen verwendet.",
        prices_vat: "Preise & MwSt.",
        use_prices: "Preise verwenden",
        use_prices_hint: "Deaktivieren, um Preise, MwSt., Anzahlungen und Zahlungsinformationen komplett auszublenden.",
        default_vat_rate: "Standard-MwSt.",
        breaks: "Pausen",
        breaks_hint: "Feste tägliche Pausen. Werden von der verfügbaren Arbeitszeit abgezogen.",
        add_break: "Pause hinzufügen",
        break_time: "Pausenzeit",
        available_work_time: "Verfügbare Arbeitszeit",
        inherit_from_workshop: "Übernimmt aus Werkstatteinstellungen",
        break_row_start: "Von", break_row_end: "Bis", break_row_days: "Wochentage",
        tab_email: "E-Mail",
        email_settings: "E-Mail-Einstellungen",
        email_from_name: "Absendername",
        email_from_name_hint: "Erscheint als „Von“-Name in jeder Kundenmail (z. B. „AutoRefit“).",
        email_reply_to: "Antwort-Adresse (Reply-To)",
        email_reply_to_hint: "Antworten Ihrer Kunden gehen an diese E-Mail-Adresse.",
        email_signature: "Signatur",
        email_signature_hint: "Wird an jede versendete Mail angehängt.",
        hu_reminder_days: "HU-Erinnerung (Tage im Voraus)",
        hu_reminder_days_hint: "Automatische Mail wird so viele Tage vor dem HU-Datum verschickt.",
      },
      billing: {
        title: "Abonnement",
        current_plan: "Aktueller Plan",
        plan: "Plan", interval: "Abrechnungszeitraum",
        monthly: "Monatlich", yearly: "Jährlich",
        status: "Status", trial_days_left: "Tage Probezeit übrig",
        trial_ends: "Probezeit endet am",
        manage_subscription: "Abonnement verwalten",
        upgrade: "Upgrade", choose_plan: "Plan auswählen",
        coming_soon: "Bald verfügbar",
        per_month: "/Monat", per_year: "/Jahr",
        features: "Funktionen",
        feat_users: "{{count}} Benutzer",
        feat_contracts: "Bis zu {{count}} Aufträge",
        feat_unlimited_contracts: "Unbegrenzte Aufträge",
        feat_all_features: "Alle Funktionen enthalten",
        free_plan: "Free-Plan",
        renews_on: "Verlängert am",
        already_on_plan: "Du bist bereits auf diesem Plan.",
        checkout_processing: "Zahlung wird verarbeitet…",
        checkout_success: "Zahlung erfolgreich. Plan wurde aktiviert.",
        checkout_failed: "Die Zahlung wurde abgebrochen oder ist abgelaufen.",
        checkout_pending: "Zahlung läuft noch. Wir aktualisieren in Kürze automatisch.",
        checkout_cancelled: "Checkout abgebrochen.",
        checkout_error: "Checkout fehlgeschlagen. Bitte erneut versuchen.",
        statuses: {
          trial: "Probezeit", active: "Aktiv", past_due: "Überfällig",
          cancelled: "Gekündigt", expired: "Abgelaufen", demo: "Demo",
        },
        plans: {
          free: "Free", basic: "Basic", pro: "Pro", enterprise: "Enterprise", demo: "Demo",
        },
        owner_only: "Nur der Inhaber kann das Abonnement verwalten.",
      },
      scan: {
        button: "Fahrzeugschein scannen",
        title: "Fahrzeugschein scannen",
        pick_hint: "Foto aufnehmen oder Datei (JPG, PNG, PDF) hochladen.",
        take_photo: "Foto aufnehmen",
        take_photo_hint: "Kamera öffnen (Mobilgeräte)",
        upload_file: "Datei hochladen",
        upload_file_hint: "JPG, PNG oder PDF",
        processing: "Dokument wird analysiert…",
        processing_hint: "Das Bild wird nur im Speicher verarbeitet und nicht gespeichert.",
        error: "Analyse fehlgeschlagen. Bitte erneut versuchen.",
        save: "Speichern",
        save_error: "Speichern fehlgeschlagen.",
        saved: "Fahrzeug gespeichert.",
        back: "Zurück",
        customer_section: "Kunde",
        vehicle_section: "Fahrzeug",
        create_customer: "Neuer Kunde",
        use_existing_customer: "Bestehender Kunde",
        pick_customer: "Kunde auswählen",
        preset_customer: "Zugewiesen an",
        existing_customer_found: "Möglicher bestehender Kunde: {{name}}",
        vehicle_exists: "Dieses Fahrzeug existiert bereits (VIN).",
        owner_mismatch: "Erkannter Halter ({{detected}}) weicht vom ausgewählten Kunden ({{selected}}) ab.",
        vin_required: "VIN ist erforderlich.",
        unknown_owner: "Unbekannter Halter",
        low_confidence_legend: "Gelb markierte Felder haben niedrige Erkennungsgenauigkeit — bitte prüfen.",
        hsn: "HSN", tsn: "TSN",
        current_registration: "Zulassung",
        engine_cc: "Hubraum (ccm)",
        power_kw: "Leistung (kW)",
        vehicle_class: "Fahrzeugklasse",
        color: "Farbe",
        max_weight: "Zul. Gesamtgewicht (kg)",
      },
      discount: {
        title: "Rabattcodes", new_code: "Neuer Code",
        code: "Code", type: "Typ", value: "Wert",
        percent: "Prozent (%)", fixed: "Festbetrag (€)", trial_extension: "Probezeit verlängern (Tage)",
        max_uses: "Max. Verwendungen", current_uses: "Verwendet",
        valid_from: "Gültig ab", valid_until: "Gültig bis",
        plan: "Plan", any: "Alle",
        interval: "Zeitraum", active: "Aktiv", inactive: "Inaktiv",
        note: "Notiz", unlimited: "Unbegrenzt", empty: "Noch keine Codes",
        owner_only: "Nur der Inhaber kann Rabattcodes verwalten.",
        validate: "Code prüfen", valid: "Code ist gültig", invalid: "Code ungültig",
        duration_months: "Dauer (Monate)",
      },
      weekdays: ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"],
    },
  },
  en: {
    translation: {
      app: { name: "Autordo", tagline: "Workshop Management" },
      nav: {
        dashboard: "Dashboard", planning: "Weekly Planning", contracts: "Contracts",
        customers: "Customers", vehicles: "Vehicles", employees: "Employees",
        absences: "Absences",
        templates: "Checklists", settings: "Settings", logout: "Sign out",
      },
      auth: {
        signin: "Sign in", signin_subtitle: "Sign in to Autordo",
        hero_title: "Plan smarter.\nWrench faster.",
        hero_subtitle: "Manage contracts, mechanic workload and inspections in one professional cockpit.",
        hourly_rate_placeholder: "Hourly rate",
        email: "Email", password: "Password", login_btn: "Sign in",
        invalid: "Invalid credentials",
        register: "Register", first_name: "First name", last_name: "Last name",
        company_name: "Company name", create_workspace: "Create workspace",
        demo_account: "Try demo account",
        demo_hint: "Explore Autordo with sample data. Resets on every demo login.",
      },
      common: {
        save: "Save", cancel: "Cancel", delete: "Delete", edit: "Edit", create: "Create",
        new: "New", search: "Search", actions: "Actions", yes: "Yes", no: "No",
        confirm: "Confirm", back: "Back", print: "Print", name: "Name", phone: "Phone",
        email: "Email", address: "Address", loading: "Loading...", empty: "No entries",
        status: "Status", priority: "Priority", notes: "Notes", description: "Description",
        close: "Close", clear_filters: "Clear filters", dialog_description: "Dialog",
        hour_short: "h", hours: "Hours", qty: "Qty", unit: "Unit",
        unit_price: "Unit price", total: "Total",
        day_short_mon: "Mon", day_short_tue: "Tue", day_short_wed: "Wed",
        day_short_thu: "Thu", day_short_fri: "Fri", day_short_sat: "Sat", day_short_sun: "Sun",
      },
      breaks: {
        error_range: "End time must be after start time",
        error_outside: "Break is outside the working hours {{start}}–{{end}}",
        error_no_days: "Select at least one weekday",
        error_overlap: "Break overlaps with another on the same day",
        error_duplicate: "Duplicate break",
      },
      workitem: {
        labor: "Labor", part: "Part", flat: "Flat rate", other: "Other",
        fixed_price: "Fixed price",
        subtotal_labor: "Labor", subtotal_parts: "Parts",
        subtotal_flat: "Flat rate", subtotal_other: "Other",
        grand_total: "Grand total",
        sub_items: "Sub-positions", add_sub_item: "Add sub-position",
        sub_total: "Sub-positions total", auto_from_sub: "Auto from sub-positions",
      },
      pricing: { net: "Net", vat: "VAT", gross: "Gross" },
      notes: {
        title: "Internal notes",
        add: "Add note",
        placeholder: "Write a note…",
        empty: "No notes yet.",
        pinned: "Pinned",
        pin: "Pin",
        unpin: "Unpin",
        author: "Author",
        by: "by",
      },
      emails: {
        title: "Email",
        send: "Send email",
        send_short: "Send",
        template: "Template",
        subject: "Subject",
        body: "Message",
        preview_note: "Signature and layout are added automatically.",
        no_email: "This customer has no email address on file.",
        sent: "Email sent",
        log_title: "Email log",
        log_empty: "No emails have been sent yet.",
        log_recipient: "Recipient",
        log_subject: "Subject",
        log_when: "Sent at",
        log_by: "By",
        log_status: "Status",
        log_auto: "Automatic (HU)",
        log_manual: "Manual",
        placeholders: "Placeholders",
        placeholders_hint: "Available: {customer_name}, {vehicle}, {license_plate}, {hu_date}, {workshop_name}, {contract_number}",
        run_hu_now: "Run HU reminders now",
        hu_reminder: "HU reminder",
        attach_pdf: "Attach PDF",
        pdf_mode_contract: "Work order",
        pdf_mode_invoice: "Invoice",
        download_pdf: "Download PDF",
        templates_title: "Email templates",
        templates_hint: "Subject and body of every template. Supports placeholders such as {customer_name}.",
        template_name: "Template name",
        template_new: "New template",
        template_saved: "Template saved",
        system_template: "System template",
      },
      vehicle_extra: {
        next_hu_date: "Next HU date",
        hu_due_in: "HU in {{days}} days",
        hu_overdue: "HU overdue",
      },
      confirm: {
        title: "Are you sure?",
        description: "Are you sure you want to delete this item? This action cannot be undone.",
      },
      dashboard: {
        title: "Dashboard", heading: "Workshop Overview", total: "Contracts", new: "New", in_progress: "In Progress",
        finished: "Finished", billed: "Billed", capacity: "Workshop Capacity",
        planned_aw: "Planned work today", occupancy: "Occupancy", overloaded: "Overloaded Employees",
        waiting: "Waiting",
        period_week: "Week", period_month: "Month", period_year: "Year",
        today: "Today's Contracts",
      },
      planning: {
        title: "Weekly Planning", week_of: "Week of", prev: "Previous", next: "Next",
        capacity: "Capacity", assigned: "Assigned", today: "Today", unassigned: "Unassigned",
        view_week: "Week", view_day: "Day", view_agenda: "Agenda",
        sum_capacity: "Total capacity", sum_planned: "Planned work",
        sum_occupancy: "Occupancy", sum_active: "Active employees",
        sum_contracts: "Contracts", sum_overloaded: "Overloaded",
        create_contract: "New contract", hours_short: "h",
      },
      contracts: {
        title: "Contracts", number: "Contract #", customer: "Customer", vehicle: "Vehicle",
        title_placeholder: "Inspection + Oil change...",
        plate: "Plate", total_aw: "Total AW", working_time: "Working Time", total_price: "Total price", planned: "Planned",
        new_contract: "New Contract", details: "Contract Details", work_items: "Work Items",
        checklist: "Checklist", audit: "History", add_work_item: "Add work item",
        completion_prompt: "All work items completed. Mark contract as Finished?",
        plan_date: "Planned date", plan_time: "Planned time", assigned_to: "Assigned to",
        apply_template: "Apply template",
        price: "Price", payment: "Payment",
        payment_status: { unpaid: "Unpaid", deposit: "Deposit", paid: "Paid" },
        deposit_amount: "Deposit", remaining: "Remaining",
      },
      statuses: {
        new: "New", planned: "Planned", in_progress: "In Progress",
        waiting_parts: "Waiting for Parts", waiting_customer: "Waiting for Customer",
        finished: "Finished", billed: "Billed", cancelled: "Cancelled",
      },
      priorities: { low: "Low", normal: "Normal", high: "High", urgent: "Urgent" },
      checklist: {
        io: "OK", nio: "Not OK", unchecked: "Open",
        items: {
          engine_oil: "Engine oil level",
          coolant: "Coolant level",
          brake_fluid: "Brake fluid",
          brakes_front: "Front brakes",
          brakes_rear: "Rear brakes",
          tire_front_left: "Front left tire",
          tire_front_right: "Front right tire",
          tire_rear_left: "Rear left tire",
          tire_rear_right: "Rear right tire",
          lights: "Lights",
          wipers: "Wipers",
          battery: "Battery",
          diagnostic: "Diagnostic scan",
          test_drive: "Test drive",
        },
        templates: { standard: "Standard inspection" },
      },
      absence: {
        title: "Absence", new: "New absence", reason: "Reason",
        start: "From", end: "To", note: "Note", day_off: "Off",
        reasons: { vacation: "Vacation", sick: "Sick leave", training: "Training", day_off: "Day off", private: "Private", other: "Other" },
        unavailable: "Not available",
        confirm_override: "Employee is absent. Assign anyway?",
        edit: "Edit absence",
        view_month: "Month", view_year: "Year",
        error_range: "End date is before start date",
        confirm_delete: "Delete absence?",
        confirm_delete_day: "Remove this day?",
        day_trim_hint: "Tip: click a single day on the calendar to remove only that day.",
        blocked_assignment: "Employee is absent – cannot be assigned",
        blocked_day_off: "Non-working day – cannot be assigned",
        weekend_day_off: "Non-working day",
      },
      employees: {
        title: "Employees", first_name: "First name", last_name: "Last name",
        role: "Role", working_days: "Working days", start: "Start", end: "End",
        break_start: "Break start", break_end: "Break end",
        daily_capacity: "Daily capacity", active: "Active",
        upcoming_absences: "Upcoming absences",
        user_account: "User account",
        user_account_hint: "Create a login for this mechanic",
        role_mechanic: "Mechanic", role_admin: "Admin",
        show_in_planning: "Show in Weekly Planning",
        show_in_planning_hint: "Owners and admins also appear as employees in the planning grid when enabled.",
      },
      customers: { title: "Customers", new_customer: "New customer", linked_vehicles: "Linked vehicles", linked_contracts: "Contracts", detail: "Customer details" },
      vehicles: {
        title: "Vehicles", brand: "Brand", model: "Model", plate: "License plate",
        vin: "VIN", mileage: "Mileage", first_registration: "First registration",
        fuel: "Fuel", engine: "Engine", new_vehicle: "New vehicle",
      },
      templates: {
        title: "Checklist Templates", new_template: "New template",
        items: "Items", apply: "Apply",
      },
      settings: {
        title: "Settings", workshop: "Workshop", workshop_name: "Workshop name",
        working_hours: "Working hours", default_language: "Default language",
        users: "Users",
        tab_workshop: "Workshop", tab_users: "Users",
        tab_billing: "Subscription", tab_discount: "Discount codes",
        time_display: "Time display",
        time_display_aw: "AW only (e.g. 80 AW)",
        time_display_hours: "Hours only (e.g. 8.0 h)",
        time_display_both: "AW and hours (e.g. 80 AW / 8.0 h)",
        time_display_hint: "10 AW = 1 hour. The internal calculation never changes, only the display.",
        hourly_rate: "Hourly rate",
        hourly_rate_hint: "Used as the default price for labor positions.",
        prices_vat: "Prices & VAT",
        use_prices: "Use prices",
        use_prices_hint: "Turn off to hide all prices, VAT, deposits and payment information.",
        default_vat_rate: "Default VAT rate",
        breaks: "Breaks",
        breaks_hint: "Fixed daily breaks. Subtracted from the available working time.",
        add_break: "Add break",
        break_time: "Break time",
        available_work_time: "Available working time",
        inherit_from_workshop: "Inherits from workshop settings",
        break_row_start: "From", break_row_end: "To", break_row_days: "Weekdays",
        tab_email: "Email",
        email_settings: "Email settings",
        email_from_name: "Sender name",
        email_from_name_hint: "Shows as the \"From\" name on every customer email (e.g. \"AutoRefit\").",
        email_reply_to: "Reply-To address",
        email_reply_to_hint: "Customer replies land in this inbox.",
        email_signature: "Signature",
        email_signature_hint: "Appended to every email you send.",
        hu_reminder_days: "HU reminder (days in advance)",
        hu_reminder_days_hint: "Automatic email is sent this many days before the HU date.",
      },
      billing: {
        title: "Subscription",
        current_plan: "Current plan",
        plan: "Plan", interval: "Billing interval",
        monthly: "Monthly", yearly: "Yearly",
        status: "Status", trial_days_left: "Trial days left",
        trial_ends: "Trial ends",
        manage_subscription: "Manage subscription",
        upgrade: "Upgrade", choose_plan: "Choose a plan",
        coming_soon: "Coming soon",
        per_month: "/mo", per_year: "/yr",
        features: "Features",
        feat_users: "{{count}} users",
        feat_contracts: "Up to {{count}} contracts",
        feat_unlimited_contracts: "Unlimited contracts",
        feat_all_features: "All features included",
        free_plan: "Free plan",
        renews_on: "Renews on",
        already_on_plan: "You are already on this plan.",
        checkout_processing: "Processing payment…",
        checkout_success: "Payment successful. Plan activated.",
        checkout_failed: "Payment was cancelled or expired.",
        checkout_pending: "Payment still pending. We will update automatically.",
        checkout_cancelled: "Checkout cancelled.",
        checkout_error: "Checkout failed. Please try again.",
        statuses: {
          trial: "Trial", active: "Active", past_due: "Past due",
          cancelled: "Cancelled", expired: "Expired", demo: "Demo",
        },
        plans: {
          free: "Free", basic: "Basic", pro: "Pro", enterprise: "Enterprise", demo: "Demo",
        },
        owner_only: "Only the workspace owner can manage the subscription.",
      },
      scan: {
        button: "Scan registration",
        title: "Scan registration document",
        pick_hint: "Take a photo or upload a file (JPG, PNG, PDF).",
        take_photo: "Take photo",
        take_photo_hint: "Opens the camera on mobile",
        upload_file: "Upload file",
        upload_file_hint: "JPG, PNG or PDF",
        processing: "Analysing document…",
        processing_hint: "Image is processed in memory only and is not stored.",
        error: "Analysis failed. Please try again.",
        save: "Save",
        save_error: "Could not save.",
        saved: "Vehicle saved.",
        back: "Back",
        customer_section: "Customer",
        vehicle_section: "Vehicle",
        create_customer: "New customer",
        use_existing_customer: "Existing customer",
        pick_customer: "Select a customer",
        preset_customer: "Assigning to",
        existing_customer_found: "Possible existing customer: {{name}}",
        vehicle_exists: "This vehicle already exists (VIN).",
        owner_mismatch: "Detected owner ({{detected}}) differs from selected customer ({{selected}}).",
        vin_required: "VIN is required.",
        unknown_owner: "Unknown owner",
        low_confidence_legend: "Fields marked amber have low OCR confidence — please review.",
        hsn: "HSN", tsn: "TSN",
        current_registration: "Registration date",
        engine_cc: "Displacement (cc)",
        power_kw: "Power (kW)",
        vehicle_class: "Vehicle class",
        color: "Colour",
        max_weight: "Max weight (kg)",
      },
      discount: {
        title: "Discount codes", new_code: "New code",
        code: "Code", type: "Type", value: "Value",
        percent: "Percentage (%)", fixed: "Fixed amount (€)", trial_extension: "Trial extension (days)",
        max_uses: "Max uses", current_uses: "Used",
        valid_from: "Valid from", valid_until: "Valid until",
        plan: "Plan", any: "Any",
        interval: "Interval", active: "Active", inactive: "Inactive",
        note: "Note", unlimited: "Unlimited", empty: "No codes yet",
        owner_only: "Only the owner can manage discount codes.",
        validate: "Validate code", valid: "Code is valid", invalid: "Invalid code",
        duration_months: "Duration (months)",
      },
      weekdays: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    },
  },
  ro: {
    translation: {
      app: { name: "Autordo", tagline: "Management Atelier" },
      nav: {
        dashboard: "Panou", planning: "Planificare săptămânală", contracts: "Contracte",
        customers: "Clienți", vehicles: "Vehicule", employees: "Angajați",
        absences: "Absențe",
        templates: "Liste de verificare", settings: "Setări", logout: "Deconectare",
      },
      auth: {
        signin: "Autentificare", signin_subtitle: "Autentifică-te în Autordo",
        hero_title: "Planifică inteligent.\nRepară mai repede.",
        hero_subtitle: "Contracte, încărcarea mecanicilor și inspecții într-un singur cockpit profesional.",
        hourly_rate_placeholder: "Tarif orar",
        email: "Email", password: "Parolă", login_btn: "Conectare",
        invalid: "Date de autentificare incorecte",
        register: "Înregistrare", first_name: "Prenume", last_name: "Nume",
        company_name: "Nume firmă", create_workspace: "Creează workspace",
        demo_account: "Cont demo",
        demo_hint: "Explorează Autordo cu date demo. Se resetează la fiecare login demo.",
      },
      common: {
        save: "Salvează", cancel: "Anulează", delete: "Șterge", edit: "Editează",
        create: "Creează", new: "Nou", search: "Caută", actions: "Acțiuni",
        yes: "Da", no: "Nu", confirm: "Confirmă", back: "Înapoi", print: "Tipărește",
        name: "Nume", phone: "Telefon", email: "Email", address: "Adresă",
        loading: "Se încarcă...", empty: "Nicio intrare", status: "Stare",
        priority: "Prioritate", notes: "Note", description: "Descriere",
        close: "Închide", clear_filters: "Șterge filtrele", dialog_description: "Dialog",
        hour_short: "h", hours: "Ore", qty: "Cant.", unit: "Unitate",
        unit_price: "Preț unitar", total: "Total",
        day_short_mon: "Lu", day_short_tue: "Ma", day_short_wed: "Mi",
        day_short_thu: "Jo", day_short_fri: "Vi", day_short_sat: "Sâ", day_short_sun: "Du",
      },
      breaks: {
        error_range: "Ora de sfârșit trebuie să fie după ora de început",
        error_outside: "Pauza este în afara programului de lucru {{start}}–{{end}}",
        error_no_days: "Selectează cel puțin o zi",
        error_overlap: "Pauza se suprapune cu alta din aceeași zi",
        error_duplicate: "Pauză duplicată",
      },
      workitem: {
        labor: "Manoperă", part: "Piesă", flat: "Preț fix", other: "Altele",
        fixed_price: "Preț fix",
        subtotal_labor: "Manoperă", subtotal_parts: "Piese",
        subtotal_flat: "Preț fix", subtotal_other: "Altele",
        grand_total: "Total general",
        sub_items: "Sub-poziții", add_sub_item: "Adaugă sub-poziție",
        sub_total: "Total sub-poziții", auto_from_sub: "Automat din sub-poziții",
      },
      pricing: { net: "Net", vat: "TVA", gross: "Brut" },
      notes: {
        title: "Notițe interne",
        add: "Adaugă notiță",
        placeholder: "Scrie o notiță…",
        empty: "Nicio notiță încă.",
        pinned: "Fixate",
        pin: "Fixează",
        unpin: "Anulează fixarea",
        author: "Autor",
        by: "de",
      },
      emails: {
        title: "E-mail",
        send: "Trimite e-mail",
        send_short: "Trimite",
        template: "Șablon",
        subject: "Subiect",
        body: "Mesaj",
        preview_note: "Semnătura și aspectul sunt adăugate automat.",
        no_email: "Clientul nu are o adresă de e-mail salvată.",
        sent: "E-mail trimis",
        log_title: "Jurnal trimiteri",
        log_empty: "Nu s-au trimis încă e-mailuri.",
        log_recipient: "Destinatar",
        log_subject: "Subiect",
        log_when: "Trimis la",
        log_by: "De",
        log_status: "Status",
        log_auto: "Automat (ITP)",
        log_manual: "Manual",
        placeholders: "Marcaje",
        placeholders_hint: "Disponibile: {customer_name}, {vehicle}, {license_plate}, {hu_date}, {workshop_name}, {contract_number}",
        run_hu_now: "Rulează reamintirile ITP acum",
        hu_reminder: "Reamintire ITP",
        attach_pdf: "Atașează PDF",
        pdf_mode_contract: "Comandă",
        pdf_mode_invoice: "Factură",
        download_pdf: "Descarcă PDF",
        templates_title: "Șabloane e-mail",
        templates_hint: "Subiectul și textul fiecărui șablon. Suportă marcaje precum {customer_name}.",
        template_name: "Nume șablon",
        template_new: "Șablon nou",
        template_saved: "Șablon salvat",
        system_template: "Șablon sistem",
      },
      vehicle_extra: {
        next_hu_date: "Următorul ITP",
        hu_due_in: "ITP în {{days}} zile",
        hu_overdue: "ITP expirat",
      },
      confirm: {
        title: "Sunteți sigur?",
        description: "Doriți să ștergeți acest element? Această acțiune nu poate fi anulată.",
      },
      dashboard: {
        title: "Panou", heading: "Prezentare generală atelier", total: "Contracte", new: "Noi", in_progress: "În lucru",
        finished: "Terminate", billed: "Facturate", capacity: "Capacitate atelier",
        planned_aw: "Timp de lucru azi", occupancy: "Ocupare",
        waiting: "În așteptare",
        period_week: "Săptămână", period_month: "Lună", period_year: "An",
        overloaded: "Angajați supraîncărcați", today: "Contracte de azi",
      },
      planning: {
        title: "Planificare săptămânală", week_of: "Săptămâna din", prev: "Anterioară",
        next: "Următoarea", capacity: "Capacitate", assigned: "Alocate", today: "Azi",
        unassigned: "Nealocate",
        view_week: "Săptămână", view_day: "Zi", view_agenda: "Agendă",
        sum_capacity: "Capacitate totală", sum_planned: "Muncă planificată",
        sum_occupancy: "Ocupare", sum_active: "Angajați activi",
        sum_contracts: "Contracte", sum_overloaded: "Supraîncărcați",
        create_contract: "Contract nou", hours_short: "h",
      },
      contracts: {
        title: "Contracte", number: "Nr. contract", customer: "Client", vehicle: "Vehicul",
        title_placeholder: "Inspecție + schimb ulei...",
        plate: "Număr înmatriculare", total_aw: "Total AW", working_time: "Timp de lucru", total_price: "Preț total", planned: "Planificat",
        new_contract: "Contract nou", details: "Detalii contract",
        work_items: "Operațiuni", checklist: "Listă verificare", audit: "Istoric",
        add_work_item: "Adaugă operațiune",
        completion_prompt: "Toate operațiunile sunt terminate. Marcați contractul ca Terminat?",
        plan_date: "Data planificată", plan_time: "Ora planificată",
        assigned_to: "Alocat lui", apply_template: "Aplică șablon",
        price: "Preț", payment: "Plată",
        payment_status: { unpaid: "Neplătit", deposit: "Avans", paid: "Plătit" },
        deposit_amount: "Avans", remaining: "Rest",
      },
      statuses: {
        new: "Nou", planned: "Planificat", in_progress: "În lucru",
        waiting_parts: "Așteaptă piese", waiting_customer: "Așteaptă client",
        finished: "Terminat", billed: "Facturat", cancelled: "Anulat",
      },
      priorities: { low: "Scăzută", normal: "Normală", high: "Înaltă", urgent: "Urgentă" },
      checklist: {
        io: "OK", nio: "Defect", unchecked: "Neverificat",
        items: {
          engine_oil: "Nivel ulei motor",
          coolant: "Nivel lichid răcire",
          brake_fluid: "Lichid frână",
          brakes_front: "Frâne față",
          brakes_rear: "Frâne spate",
          tire_front_left: "Anvelopă față stânga",
          tire_front_right: "Anvelopă față dreapta",
          tire_rear_left: "Anvelopă spate stânga",
          tire_rear_right: "Anvelopă spate dreapta",
          lights: "Iluminare",
          wipers: "Ștergătoare",
          battery: "Baterie",
          diagnostic: "Scanare diagnostic",
          test_drive: "Probă de drum",
        },
        templates: { standard: "Inspecție standard" },
      },
      absence: {
        title: "Absență", new: "Absență nouă", reason: "Motiv",
        start: "De la", end: "Până la", note: "Notă", day_off: "Liber",
        reasons: { vacation: "Concediu", sick: "Concediu medical", training: "Instruire", day_off: "Zi liberă", private: "Privat", other: "Altul" },
        unavailable: "Indisponibil",
        confirm_override: "Angajatul este absent. Atribuiți oricum?",
        edit: "Editează absență",
        view_month: "Lună", view_year: "An",
        error_range: "Data de sfârșit este înainte de data de început",
        confirm_delete: "Ștergeți absența?",
        confirm_delete_day: "Eliminați această zi?",
        day_trim_hint: "Sfat: apăsați o singură zi din calendar pentru a elimina doar ziua respectivă.",
        blocked_assignment: "Angajatul este absent – atribuire imposibilă",
        blocked_day_off: "Zi nelucrătoare – atribuire imposibilă",
        weekend_day_off: "Zi nelucrătoare",
      },
      employees: {
        title: "Angajați", first_name: "Prenume", last_name: "Nume",
        role: "Rol", working_days: "Zile lucrătoare", start: "Început", end: "Sfârșit",
        break_start: "Pauză început", break_end: "Pauză sfârșit",
        daily_capacity: "Capacitate zilnică", active: "Activ",
        upcoming_absences: "Absențe viitoare",
        user_account: "Cont utilizator",
        user_account_hint: "Creează cont de autentificare pentru acest mecanic",
        role_mechanic: "Mecanic", role_admin: "Administrator",
        show_in_planning: "Afișează în planificarea săptămânală",
        show_in_planning_hint: "Proprietarii și admin-ii apar și ei ca angajați în grila de planificare dacă este activat.",
      },
      customers: { title: "Clienți", new_customer: "Client nou", linked_vehicles: "Vehicule asociate", linked_contracts: "Contracte", detail: "Detalii client" },
      vehicles: {
        title: "Vehicule", brand: "Marcă", model: "Model", plate: "Număr de înmatriculare",
        vin: "VIN", mileage: "Kilometraj", first_registration: "Prima înmatriculare",
        fuel: "Combustibil", engine: "Motor", new_vehicle: "Vehicul nou",
      },
      templates: {
        title: "Șabloane liste verificare", new_template: "Șablon nou",
        items: "Elemente", apply: "Aplică",
      },
      settings: {
        title: "Setări", workshop: "Atelier", workshop_name: "Nume atelier",
        working_hours: "Program de lucru", default_language: "Limbă implicită",
        users: "Utilizatori",
        tab_workshop: "Atelier", tab_users: "Utilizatori",
        tab_billing: "Abonament", tab_discount: "Coduri reducere",
        time_display: "Afișare timp",
        time_display_aw: "Doar AW (ex. 80 AW)",
        time_display_hours: "Doar ore (ex. 8,0 h)",
        time_display_both: "AW și ore (ex. 80 AW / 8,0 h)",
        time_display_hint: "10 AW = 1 oră. Calculul intern nu se modifică, doar afișarea.",
        hourly_rate: "Tarif orar",
        hourly_rate_hint: "Se folosește ca preț implicit pentru pozițiile de manoperă.",
        prices_vat: "Prețuri & TVA",
        use_prices: "Folosește prețuri",
        use_prices_hint: "Dezactivează pentru a ascunde toate prețurile, TVA, avansurile și informațiile de plată.",
        default_vat_rate: "TVA implicit",
        breaks: "Pauze",
        breaks_hint: "Pauze zilnice fixe. Se scad din timpul de lucru disponibil.",
        add_break: "Adaugă pauză",
        break_time: "Timp de pauză",
        available_work_time: "Timp de lucru disponibil",
        inherit_from_workshop: "Moștenit din setările atelierului",
        break_row_start: "De la", break_row_end: "Până la", break_row_days: "Zilele săptămânii",
        tab_email: "E-mail",
        email_settings: "Setări e-mail",
        email_from_name: "Nume expeditor",
        email_from_name_hint: "Apare ca nume „De la” în fiecare e-mail către client.",
        email_reply_to: "Adresa de răspuns",
        email_reply_to_hint: "Răspunsurile clienților ajung la această adresă.",
        email_signature: "Semnătură",
        email_signature_hint: "Se adaugă la fiecare e-mail trimis.",
        hu_reminder_days: "Reamintire ITP (zile înainte)",
        hu_reminder_days_hint: "E-mailul automat se trimite cu atâtea zile înainte de data ITP.",
      },
      billing: {
        title: "Abonament",
        current_plan: "Plan curent",
        plan: "Plan", interval: "Interval facturare",
        monthly: "Lunar", yearly: "Anual",
        status: "Status", trial_days_left: "Zile rămase din probă",
        trial_ends: "Proba expiră la",
        manage_subscription: "Gestionează abonamentul",
        upgrade: "Upgrade", choose_plan: "Alege un plan",
        coming_soon: "În curând",
        per_month: "/lună", per_year: "/an",
        features: "Funcții",
        feat_users: "{{count}} utilizatori",
        feat_contracts: "Până la {{count}} contracte",
        feat_unlimited_contracts: "Contracte nelimitate",
        feat_all_features: "Toate funcțiile incluse",
        free_plan: "Plan Free",
        renews_on: "Se reînnoiește la",
        already_on_plan: "Ești deja pe acest plan.",
        checkout_processing: "Se procesează plata…",
        checkout_success: "Plată reușită. Planul a fost activat.",
        checkout_failed: "Plata a fost anulată sau a expirat.",
        checkout_pending: "Plata este în așteptare. Vom actualiza automat.",
        checkout_cancelled: "Checkout anulat.",
        checkout_error: "Checkout eșuat. Încearcă din nou.",
        statuses: {
          trial: "Probă", active: "Activ", past_due: "Restant",
          cancelled: "Anulat", expired: "Expirat", demo: "Demo",
        },
        plans: {
          free: "Free", basic: "Basic", pro: "Pro", enterprise: "Enterprise", demo: "Demo",
        },
        owner_only: "Doar proprietarul poate gestiona abonamentul.",
      },
      scan: {
        button: "Scanează talon",
        title: "Scanează talonul vehiculului",
        pick_hint: "Fă o poză sau încarcă un fișier (JPG, PNG, PDF).",
        take_photo: "Fă poză",
        take_photo_hint: "Deschide camera pe mobil",
        upload_file: "Încarcă fișier",
        upload_file_hint: "JPG, PNG sau PDF",
        processing: "Se analizează documentul…",
        processing_hint: "Imaginea este procesată doar în memorie și nu este stocată.",
        error: "Analiză eșuată. Încearcă din nou.",
        save: "Salvează",
        save_error: "Salvarea a eșuat.",
        saved: "Vehicul salvat.",
        back: "Înapoi",
        customer_section: "Client",
        vehicle_section: "Vehicul",
        create_customer: "Client nou",
        use_existing_customer: "Client existent",
        pick_customer: "Selectează client",
        preset_customer: "Se atribuie lui",
        existing_customer_found: "Posibil client existent: {{name}}",
        vehicle_exists: "Acest vehicul există deja (VIN).",
        owner_mismatch: "Proprietarul detectat ({{detected}}) diferă de clientul selectat ({{selected}}).",
        vin_required: "VIN este obligatoriu.",
        unknown_owner: "Proprietar necunoscut",
        low_confidence_legend: "Câmpurile marcate galben au încredere OCR scăzută — te rog verifică.",
        hsn: "HSN", tsn: "TSN",
        current_registration: "Data înmatriculării",
        engine_cc: "Cilindree (cc)",
        power_kw: "Putere (kW)",
        vehicle_class: "Clasă vehicul",
        color: "Culoare",
        max_weight: "Greutate max (kg)",
      },
      discount: {
        title: "Coduri de reducere", new_code: "Cod nou",
        code: "Cod", type: "Tip", value: "Valoare",
        percent: "Procent (%)", fixed: "Sumă fixă (€)", trial_extension: "Extindere probă (zile)",
        max_uses: "Utilizări max.", current_uses: "Folosit",
        valid_from: "Valabil de la", valid_until: "Valabil până la",
        plan: "Plan", any: "Toate",
        interval: "Interval", active: "Activ", inactive: "Inactiv",
        note: "Notă", unlimited: "Nelimitat", empty: "Niciun cod încă",
        owner_only: "Doar proprietarul poate gestiona codurile de reducere.",
        validate: "Validează codul", valid: "Cod valid", invalid: "Cod invalid",
        duration_months: "Durată (luni)",
      },
      weekdays: ["Lu", "Ma", "Mi", "Jo", "Vi", "Sâ", "Du"],
    },
  },
};

const stored = typeof window !== "undefined" ? localStorage.getItem("autoflow_lang") : null;

i18n.use(initReactI18next).init({
  resources,
  lng: stored || "de",
  fallbackLng: "en",
  interpolation: { escapeValue: false },
});

export default i18n;
